import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import type { MessageAttachment } from '@erp/rpc';
import { DocumentAssociationType } from '@erp/shared-types';
import { useUploadEntityDocument } from '@/api/rpc/hooks/core/useEntityDocuments';
import type { ChatInputAttachment } from './ChatInputAttachments';

/** A file staged in the composer, including ones still uploading. */
interface PendingChatAttachment extends ChatInputAttachment {
  mimeType: string | null;
  fileSizeBytes: number | null;
}

/** Ready files lifted off the composer, plus how to put them back. */
interface TakenChatAttachments {
  attachments: MessageAttachment[] | undefined;
  restore: () => void;
}

function toMessageAttachments(
  pending: readonly PendingChatAttachment[],
): MessageAttachment[] | undefined {
  if (pending.length === 0) return undefined;
  return pending.map((att) => ({
    documentId: att.documentId,
    fileName: att.fileName,
    mimeType: att.mimeType,
    fileSizeBytes: att.fileSizeBytes,
  }));
}

/**
 * Files staged in the assistant composer. The paperclip and a file drop share
 * this: each file uploads as a chat attachment, then rides along on the next
 * message. A send that the assistant refuses puts them back.
 */
export function useChatAttachments(threadId: string | null) {
  const [pending, setPending] = useState<PendingChatAttachment[]>([]);
  const pendingRef = useRef(pending);
  // Bumped by clear() so an upload started before New chat cannot put files back.
  const uploadGenerationRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync } = useUploadEntityDocument();

  // Layout, not a passive effect: the send button enables on this render, and
  // a click must not read the previous attachment list.
  useLayoutEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  const uploadFiles = useCallback(
    async (files: readonly File[]) => {
      const generation = uploadGenerationRef.current;
      for (const file of files) {
        if (generation !== uploadGenerationRef.current) return;
        const tempId = crypto.randomUUID();
        const attachment: PendingChatAttachment = {
          documentId: tempId,
          fileName: file.name,
          uploading: true,
          mimeType: file.type || null,
          fileSizeBytes: file.size,
        };
        setPending((prev) => [...prev, attachment]);

        const entityId = threadId ?? crypto.randomUUID();
        try {
          const result = await mutateAsync({
            entityType: 'ai_thread_message',
            entityId,
            file,
            associationType: DocumentAssociationType.CHAT_ATTACHMENT,
          });
          if (generation !== uploadGenerationRef.current) return;
          setPending((prev) =>
            prev.map((att) =>
              att.documentId === tempId
                ? {
                    ...att,
                    documentId: result.documentId,
                    uploading: false,
                    mimeType: result.mimeType,
                    fileSizeBytes: result.fileSizeBytes,
                  }
                : att,
            ),
          );
        } catch {
          if (generation !== uploadGenerationRef.current) return;
          setPending((prev) => prev.filter((att) => att.documentId !== tempId));
        }
      }
    },
    [mutateAsync, threadId],
  );

  const onFileInputChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const picked = event.target.files;
      if (!picked?.length) return;
      const files = Array.from(picked);
      // Reset so the same file can be chosen again.
      event.target.value = '';
      await uploadFiles(files);
    },
    [uploadFiles],
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const remove = useCallback((documentId: string) => {
    setPending((prev) => prev.filter((att) => att.documentId !== documentId));
  }, []);

  const clear = useCallback(() => {
    uploadGenerationRef.current += 1;
    setPending([]);
  }, []);

  /** Uploaded files leave the composer. In-flight uploads stay put. */
  const takeReady = useCallback((): TakenChatAttachments => {
    const current = pendingRef.current;
    const ready = current.filter((att) => !att.uploading);
    setPending(current.filter((att) => att.uploading));
    return {
      attachments: toMessageAttachments(ready),
      restore: () => {
        setPending((next) => {
          const kept = new Set(next.map((att) => att.documentId));
          return [...ready.filter((att) => !kept.has(att.documentId)), ...next];
        });
      },
    };
  }, []);

  return {
    pending,
    uploading: pending.some((att) => att.uploading),
    uploadFiles,
    fileInputRef,
    onFileInputChange,
    openFilePicker,
    remove,
    clear,
    takeReady,
  };
}
